import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {KTSVG} from '../../../../_metronic/helpers'
import {useLocation, useNavigate} from 'react-router-dom'
import {useAuth, getAuth} from '../../auth'
import {useOperationsAccess} from '../core/useOperationsAccess'
import {useOperationsScopeUx} from '../core/operationsScopeUx'
import {
  fetchReportAttachmentDownloadBlob,
  fetchReportAttachmentViewBlob,
  updatedSubmittedReport,
  getConsumptionByVoyageReport,
  getConsumptionByVoyage,
  listReportAttachments,
} from '../core/_requests'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import {ReportStatus, Submenu, Voyage} from '../core/_models'
import { toast } from 'react-toastify'
import { getApiErrorMessage } from '../../../shared/utils/apiErrorMessage'
import {
  formatReportDateTimeDisplay,
  formatReportDateTimeFileToken,
} from '../core/reportDateTime'
import {
  formatConsumptionMetric,
  formatConsumptionStoredValue,
  isConsumptionMultiRowFieldLabel,
  normalizeScalarForDisplay,
} from '../core/valueNormalization'
import {
  renderConsumptionRobSectionHtml,
  renderGenericTabBodyHtml,
  renderMiscConsumablesTableHtml,
  renderNoFieldsBodyHtml,
  renderReportDocumentEndHtml,
  renderReportDocumentStartHtml,
  renderSectionHtml,
  ReportExportMeta,
} from '../core/reportExportHtml'
import {
  isFuelChangeoverReportName,
  synthesizeTankWiseFuelRows,
} from './fuelChangeoverReportSchema'
import {
  resolvePreviousReportConsumption,
} from './AddConsumptionROB'
import {auto} from '@popperjs/core'

const sortSubmenusByOrder = (submenus: any[]) =>
  [...submenus].sort((left, right) => {
    const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER
    const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER
    if (leftOrder !== rightOrder) return leftOrder - rightOrder
    return Number(left.id ?? 0) - Number(right.id ?? 0)
  })

const sortFieldsByOrder = (fields: any[]) =>
  [...fields].sort((left, right) => {
    const leftOrder = left.order ?? Number.MAX_SAFE_INTEGER
    const rightOrder = right.order ?? Number.MAX_SAFE_INTEGER
    if (leftOrder !== rightOrder) return leftOrder - rightOrder
    return Number(left.id ?? 0) - Number(right.id ?? 0)
  })

const normalizePreviewFieldLabel = (label: any) => String(label || '').trim().toLowerCase()

const hasMeaningfulPreviewValue = (value: any) => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

const parsePreviewJsonValue = (raw: any): any => {
  if (raw === null || raw === undefined || raw === '') return null
  if (typeof raw !== 'string') return raw
  try {
    return JSON.parse(raw)
  } catch {
    return raw
  }
}

const toPreviewStringArray = (raw: any): string[] => {
  const parsed = parsePreviewJsonValue(raw)
  if (parsed === null || parsed === undefined || parsed === '') return []
  if (Array.isArray(parsed)) return parsed.map((item) => String(item ?? ''))
  return [String(parsed ?? '')]
}

const toPreviewString2D = (raw: any): string[][] => {
  const parsed = parsePreviewJsonValue(raw)
  if (!Array.isArray(parsed)) return []
  return parsed.map((row: any) =>
    Array.isArray(row) ? row.map((item) => String(item ?? '')) : [String(row ?? '')]
  )
}

interface PreviewReportLocationState {
  formData: {
    [tabId: number]: {
      [label: string]: {
        id: string
        value: any
      }
    }
  }
  tabs: Submenu[]
  assignmentId: number
  selectedVoyage: Voyage
  stepCompletionStatus: boolean[]
  selectedReport: any
  reportDate: string
  readOnly: boolean
  allValues: any
  reportId?: number
  consumptionPreviewData?: any
  vesselId: number
  pendingBunkerAttachments?: Array<{
    id: string
    reportBlock?: number
    documentName: string
    remarks: string
    file?: File
    fileName?: string
  }>
}

const PreviewReportModal: FC = () => {
  // const step = tabs[currentStep];
  // const roleId = sessionStorage.getItem("roleId");
  const {currentUser} = useAuth()
  const {isSuperAdmin} = useOperationsAccess()
  const scopeUx = useOperationsScopeUx(isSuperAdmin)
  const location = useLocation()
  const [remarks, setRemarks] = useState('')
  const [remarkSubmitting, setRemarkSubmitting] = useState(false)
  const remarkSubmitRef = useRef(false)
  const locationState = (location.state || {}) as Partial<PreviewReportLocationState>
  const {
    formData = {},
    tabs = [],
    assignmentId = 0,
    selectedVoyage,
    stepCompletionStatus = [],
    selectedReport,
    reportDate = '',
    readOnly = false,
    allValues = [],
    reportId: reportIdFromState,
    consumptionPreviewData,
    vesselId = 0,
    pendingBunkerAttachments = [],
  } = locationState

  const orderedTabs = useMemo(() => sortSubmenusByOrder(tabs || []), [tabs])
  const formattedReportDate = useMemo(
    () => formatReportDateTimeDisplay(reportDate),
    [reportDate]
  )
  const reportDateFileToken = useMemo(
    () => formatReportDateTimeFileToken(reportDate),
    [reportDate]
  )

  const isFuelChangeoverReport = isFuelChangeoverReportName(
    String(
      selectedReport?.reportType ??
      selectedReport?.name ??
      (selectedReport as any)?.reportName ??
      (location.state as any)?.selectedReportName ??
      ''
    )
  )

  const resolvedVesselId = useMemo(() => {
    const candidates = [
      vesselId,
      (selectedVoyage as any)?.vessel?.id,
      (selectedVoyage as any)?.vesselId,
      (selectedReport as any)?.vessel?.id,
      (selectedReport as any)?.vesselId,
      (selectedReport as any)?.voyageDto?.vessel?.id,
      (selectedReport as any)?.assignment?.vessel?.id,
    ]
    for (const candidate of candidates) {
      const n = Number(candidate)
      if (Number.isFinite(n) && n > 0) return n
    }
    return 0
  }, [vesselId, selectedVoyage, selectedReport])

  const navigate = useNavigate()
  const reportRef = useRef<HTMLDivElement>(null)
  const [activeAccordion, setActiveAccordion] = useState<number | null>(orderedTabs[0]?.id || null)
  const [consumptionData, setConsumptionData] = useState<any | null>(null)
  const [consumptionLoading, setConsumptionLoading] = useState(false)
  const [consumptionError, setConsumptionError] = useState<string | null>(null)
  const [serverReportAttachments, setServerReportAttachments] = useState<any[]>([])
  const [attachmentSourceReportId, setAttachmentSourceReportId] = useState<number | null>(null)

  const candidateReportIds = useMemo(() => {
    const candidateIds: number[] = []

    const pushIfValid = (value: any) => {
      const n = Number(value)
      if (Number.isFinite(n) && n > 0 && !candidateIds.includes(n)) {
        candidateIds.push(n)
      }
    }

    const pushReportValueId = (item: any) => {
      pushIfValid(item?.reportId)
      pushIfValid(item?.report)
    }

    pushIfValid(reportIdFromState)

    if (Array.isArray((selectedReport as any)?.values)) {
      for (const item of (selectedReport as any).values) {
        pushReportValueId(item)
      }
    }

    if (Array.isArray(allValues)) {
      for (const item of allValues as any[]) {
        pushReportValueId(item)
      }
    }

    if (readOnly) {
      pushIfValid((selectedReport as any)?.id)
      pushIfValid((selectedReport as any)?.reportId)
    }

    return candidateIds
  }, [selectedReport, allValues, reportIdFromState, readOnly])

  const resolvedReportId = useMemo(() => {
    return candidateReportIds.length > 0 ? candidateReportIds[0] : NaN
  }, [candidateReportIds])

  const derivedConsumptionPreviewData = useMemo(() => {
    const consumptionTab = orderedTabs.find(
      (tab) => String(tab?.name || '').trim().toLowerCase() === 'consumption & rob'
    )
    if (!consumptionTab?.id) return null

    const tabValues = formData?.[consumptionTab.id]
    const tabEntries = Object.values(tabValues || {}) as Array<{label?: string; value?: any}>
    if (tabEntries.length === 0) return null

    const findFirst = (predicate: (label: string) => boolean) =>
      tabEntries.find((entry) => predicate(normalizePreviewFieldLabel(entry?.label)))

    const findNth = (predicate: (label: string) => boolean, n: number) => {
      const matches = tabEntries.filter((entry) => predicate(normalizePreviewFieldLabel(entry?.label)))
      return matches[n - 1]
    }

    const isInitialRobLabel = (label: string) =>
      label.includes('initial rob') ||
      label.includes('inital rob') ||
      (label.includes('opening') && label.includes('rob'))

    const isRobLabel = (label: string) => label.includes('rob') && !isInitialRobLabel(label)

    const tankNames = toPreviewStringArray(findFirst((label) => label.includes('tank'))?.value)
    const q88Grades = toPreviewStringArray(
      findFirst((label) => label.includes('q88') || label.includes('grade'))?.value
    )
    const sulphurPercentages = toPreviewStringArray(
      findFirst((label) => label.includes('sulphur'))?.value
    )
    const allocationStatuses = toPreviewStringArray(
      findFirst((label) => label.includes('allocation') || label.includes('status'))?.value
    )
    const fuelTypes = toPreviewStringArray(
      findFirst((label) => label.includes('fuel type'))?.value
    )
    const initialRobs = toPreviewStringArray(findFirst(isInitialRobLabel)?.value)
    const outerRobs = toPreviewStringArray(findNth(isRobLabel, 1)?.value)
    const machineries = toPreviewString2D(
      findFirst((label) => label.includes('machinery'))?.value
    )
    const operationTypes = toPreviewString2D(
      findFirst((label) => label.includes('operation type'))?.value
    )
    const consumedRows = toPreviewString2D(
      findFirst((label) => label.includes('consumed'))?.value
    )
    const innerRobs = toPreviewString2D(findNth(isRobLabel, 2)?.value)

    const rowCount = Math.max(
      tankNames.length,
      q88Grades.length,
      fuelTypes.length,
      initialRobs.length,
      outerRobs.length,
      machineries.length,
      0
    )

    if (rowCount === 0) return null

    const rows = Array.from({length: rowCount})
      .map((_, rowIndex) => {
        const tankName = String(tankNames[rowIndex] ?? '').trim()
        const q88Grade = String(q88Grades[rowIndex] ?? '').trim()
        const sulphurPercentage = String(sulphurPercentages[rowIndex] ?? '').trim()
        const allocationStatus = String(allocationStatuses[rowIndex] ?? '').trim()
        const fieldType = String(fuelTypes[rowIndex] ?? '').trim()
        const initialRob = String(initialRobs[rowIndex] ?? '').trim()
        const lastRob = String(outerRobs[rowIndex] ?? '').trim()
        const machineryRow = machineries[rowIndex] ?? []
        const operationTypeRow = operationTypes[rowIndex] ?? []
        const consumedRow = consumedRows[rowIndex] ?? []
        const innerRobRow = innerRobs[rowIndex] ?? []
        const machineryCount = Math.max(
          machineryRow.length,
          operationTypeRow.length,
          consumedRow.length,
          innerRobRow.length,
          0
        )

        const machineriesForRow = Array.from({length: machineryCount})
          .map((__, machineryIndex) => ({
            machinery: String(machineryRow[machineryIndex] ?? '').trim(),
            operationType: String(operationTypeRow[machineryIndex] ?? '').trim(),
            consumed: String(consumedRow[machineryIndex] ?? '').trim(),
            rob: String(innerRobRow[machineryIndex] ?? '').trim(),
          }))
          .filter((machinery) =>
            Object.values(machinery).some((value) => hasMeaningfulPreviewValue(value))
          )

        if (
          !tankName &&
          !q88Grade &&
          !fieldType &&
          !initialRob &&
          !lastRob &&
          machineriesForRow.length === 0
        ) {
          return null
        }

        return {
          tankName,
          q88Grade,
          sulphurPercentage,
          allocationStatus,
          fieldType,
          initialRob,
          lastRob,
          machineries: machineriesForRow,
        }
      })
      .filter(Boolean)

    return rows.length > 0 ? {rows} : null
  }, [formData, orderedTabs])

  const resolvedConsumptionPreviewData =
    consumptionPreviewData?.rows?.length || consumptionPreviewData?.tanks?.length
      ? consumptionPreviewData
      : derivedConsumptionPreviewData

  const displayRemark = useMemo(() => {
    const fromReport =
      (typeof selectedReport?.remark === 'string' && selectedReport.remark.trim()) ||
      (typeof selectedReport?.remarks === 'string' && selectedReport.remarks.trim()) ||
      ''
    if (fromReport) return fromReport

    const fromConsumption =
      (typeof consumptionData?.remarks === 'string' && consumptionData.remarks.trim()) ||
      (typeof resolvedConsumptionPreviewData?.remarks === 'string' && resolvedConsumptionPreviewData.remarks.trim()) ||
      ''
    return fromConsumption
  }, [selectedReport?.remark, selectedReport?.remarks, consumptionData?.remarks, resolvedConsumptionPreviewData?.remarks])

  const canAddReviewRemark = readOnly && !scopeUx.isCrew
  const shouldShowRemarksSection = Boolean(displayRemark) || canAddReviewRemark

  // Voyage id can be missing from navigation state (e.g. list opened without a voyage
  // selection) — fall back to the ids carried on the report itself so the
  // consumption fetch (rows + remarks) still succeeds.
  const resolvedVoyageId = useMemo(() => {
    const candidates = [
      (selectedVoyage as any)?.id,
      (selectedReport as any)?.voyage,
      (selectedReport as any)?.voyage?.id,
      (selectedReport as any)?.voyageDto?.id,
      (selectedReport as any)?.voyageId,
      (selectedReport as any)?.assignment?.voyage?.id,
    ]
    for (const candidate of candidates) {
      const n = Number(candidate)
      if (Number.isFinite(n) && n > 0) return n
    }
    return NaN
  }, [selectedVoyage, selectedReport])

  useEffect(() => {
    const fetchConsumption = async () => {
      const voyageId = resolvedVoyageId
      const reportId = Number(resolvedReportId)

      if (!Number.isFinite(voyageId) || voyageId <= 0) {
        if (resolvedConsumptionPreviewData?.rows?.length) {
          setConsumptionData(resolvedConsumptionPreviewData)
        }
        return
      }

      setConsumptionLoading(true)
      setConsumptionError(null)
      try {
        let data: any = null
        if (Number.isFinite(reportId) && reportId > 0) {
          try {
            data = await getConsumptionByVoyageReport(voyageId, reportId)
          } catch {
            // fallback below
          }
        }

        if (!data || !data.rows?.length) {
          const authObj = getAuth()
          const resolvedToken =
            authObj?.auth?.jwt || sessionStorage.getItem('jwt') || sessionStorage.getItem('token') || ''
          data = await resolvePreviousReportConsumption(
            reportId > 0 ? reportId : null,
            voyageId,
            resolvedVesselId,
            resolvedToken
          )
        }

        if (data && (data.rows?.length || data.lubeOils?.length || data.remarks)) {
          setConsumptionData(data)
          setConsumptionError(null)
        } else if (resolvedConsumptionPreviewData) {
          setConsumptionData(
            data?.remarks
              ? {...resolvedConsumptionPreviewData, remarks: data.remarks}
              : resolvedConsumptionPreviewData
          )
          setConsumptionError(null)
        } else {
          setConsumptionData(null)
        }
      } catch (err: any) {
        console.error('Failed to load consumption data', err)
        if (resolvedConsumptionPreviewData?.rows?.length) {
          setConsumptionData(resolvedConsumptionPreviewData)
          setConsumptionError(null)
        } else {
          setConsumptionError(err?.message || 'Failed to load consumption data')
        }
      } finally {
        setConsumptionLoading(false)
      }
    }
    fetchConsumption()
  }, [resolvedVoyageId, resolvedReportId, resolvedVesselId, resolvedConsumptionPreviewData, isFuelChangeoverReport])

  useEffect(() => {
    if (candidateReportIds.length === 0) {
      setServerReportAttachments([])
      setAttachmentSourceReportId(null)
      return
    }

    let active = true

    const loadAttachments = async () => {
      for (const reportId of candidateReportIds) {
        try {
          const items = await listReportAttachments(reportId)
          if (!active) return
          if (Array.isArray(items) && items.length > 0) {
            setServerReportAttachments(items)
            setAttachmentSourceReportId(reportId)
            return
          }
        } catch {
          // try next candidate id
        }
      }

      if (!active) return
      setServerReportAttachments([])
      setAttachmentSourceReportId(candidateReportIds[0] || null)
    }

    void loadAttachments()

    return () => {
      active = false
    }
  }, [candidateReportIds])

  function safeParseArray<T>(value: any): T {
    if (value === null || value === undefined) return [] as unknown as T
    if (Array.isArray(value)) return value as unknown as T
    if (typeof value !== 'string') return [] as unknown as T
    try {
      const parsed = JSON.parse(value)
      return (Array.isArray(parsed) ? parsed : []) as unknown as T
    } catch {
      return [] as unknown as T
    }
  }

  const normalize = (value: any) => String(value || '').trim().toLowerCase()
  const isBunkerReceivedRowsLabel = (label: any) => normalize(label) === 'bunker received rows'
  const isBunkerAttachmentLabel = (label: any) => {
    const normalizedLabel = normalize(label)
    return normalizedLabel === 'bunker attachments' || normalizedLabel === 'attachment rows'
  }

  const renderBunkerReceivedRows = (raw: any, attachmentsRaw?: any): JSX.Element | null => {
    const rows = safeParseArray<
      Array<{
        reportBlock?: number
        allocationOnly?: boolean
        dateUtc?: string
        timeUtc?: string
        port?: string
        fuelType?: string
        quantity?: string
        storageQuantity?: string
        supplierName?: string
        sulfurPercentage?: string
        storage?: string
      }>
    >(raw)

    if (!Array.isArray(rows) || rows.length === 0) return null

    type BlockAttachment = {
      id?: number
      reportId?: number
      reportBlock: number
      documentName: string
      fileName: string
      remarks?: string
      status: 'uploaded' | 'pending'
    }

    const parseAttachmentPayload = (payload: any): BlockAttachment[] => {
      if (!payload) return []
      let parsed: any = null
      if (typeof payload === 'string') {
        try {
          parsed = JSON.parse(payload)
        } catch {
          return []
        }
      } else {
        parsed = payload
      }

      const uploaded = Array.isArray(parsed?.uploaded)
        ? parsed.uploaded.map((item: any) => ({
            id: Number(item?.id),
            reportId: Number(item?.reportId),
            reportBlock: Number(item?.reportBlock) > 0 ? Number(item?.reportBlock) : 1,
            documentName: String(item?.documentName || '--'),
            fileName: String(item?.fileName || '--'),
            remarks: String(item?.remarks || ''),
            status: 'uploaded' as const,
          }))
        : []

      const pending = Array.isArray(parsed?.pending)
        ? parsed.pending.map((item: any) => ({
            reportBlock: Number(item?.reportBlock) > 0 ? Number(item?.reportBlock) : 1,
            documentName: String(item?.documentName || '--'),
            fileName: String(item?.fileName || '--'),
            remarks: String(item?.remarks || ''),
            status: 'pending' as const,
          }))
        : []

      return [...uploaded, ...pending]
    }

    const reportId =
      Number.isFinite(Number(attachmentSourceReportId)) && Number(attachmentSourceReportId) > 0
        ? Number(attachmentSourceReportId)
        : resolvedReportId
    const payloadAttachments = parseAttachmentPayload(attachmentsRaw)

    const normalizeBaseName = (value: string) => {
      const normalized = String(value || '').replace(/\\/g, '/').trim()
      if (!normalized) return ''
      const parts = normalized.split('/')
      return normalize(parts[parts.length - 1] || '')
    }

    const pendingFilesByName = new Map<string, File>()
    pendingBunkerAttachments.forEach((item: any) => {
      if (!item?.file) return
      const f = item.file as File
      if (item.fileName) pendingFilesByName.set(String(item.fileName), f)
      if (f.name) pendingFilesByName.set(String(f.name), f)
      pendingFilesByName.set(normalizeBaseName(f.name), f)
      pendingFilesByName.set(normalize(String(f.name)), f)
    })

    const resolvePendingFileForAttachment = (attachment: BlockAttachment): File | null => {
      const fn = String(attachment.fileName || '')
      const base = normalizeBaseName(fn)
      const matchEntry = Array.from(pendingFilesByName.entries()).find(
        ([key]) => normalizeBaseName(key) === base || normalize(key) === normalize(fn)
      )
      return (
        pendingFilesByName.get(fn) ||
        pendingFilesByName.get(base) ||
        pendingFilesByName.get(normalize(fn)) ||
        matchEntry?.[1] ||
        null
      )
    }

    const uploadedFromApi: Array<{
      id: number
      reportId?: number
      documentName: string
      fileName: string
      remarks: string
    }> = (serverReportAttachments || []).map((item: any) => ({
      id: Number(item?.id),
      reportId: Number(item?.reportId),
      documentName: String(item?.documentName || '--'),
      fileName: String(item?.fileName || '--'),
      remarks: String(item?.remarks || ''),
    }))

    const openBlobInNewTab = (blob: Blob) => {
      const objectUrl = URL.createObjectURL(blob)
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    }

    const downloadBlobAsFile = (blob: Blob, filename: string) => {
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = filename || 'attachment'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    }

    const viewPendingFile = (file: File) => {
      const objectUrl = URL.createObjectURL(file)
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    }

    const downloadPendingFile = (file: File, filename: string) => {
      const objectUrl = URL.createObjectURL(file)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = filename || file.name || 'attachment'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    }

    const blocks = Array.from(
      new Set(rows.map((row) => (Number(row?.reportBlock) > 0 ? Number(row?.reportBlock) : 1)))
    ).sort((a, b) => a - b)

    const rowsByBlock = (block: number) =>
      rows.filter((row) => (Number(row?.reportBlock) > 0 ? Number(row?.reportBlock) : 1) === block)

    const attachmentsForBlock = (block: number): BlockAttachment[] => {
      const fromPayload = payloadAttachments.filter((item) => item.reportBlock === block)
      const fromPendingState = (pendingBunkerAttachments || [])
        .filter((item: any) => (Number(item?.reportBlock) > 0 ? Number(item?.reportBlock) : 1) === block)
        .map((item: any) => ({
          reportBlock: block,
          documentName: String(item?.documentName || '--'),
          fileName: String(item?.fileName || item?.file?.name || '--'),
          remarks: String(item?.remarks || ''),
          status: 'pending' as const,
        }))

      const merged = [...fromPayload, ...fromPendingState]
      const deduped = new Map<string, BlockAttachment>()
      merged.forEach((item) => {
        const key = `${normalize(item.documentName)}|${normalize(item.fileName)}`
        if (!deduped.has(key)) deduped.set(key, item)
      })

      return Array.from(deduped.values())
    }

    const resolveUploadedMeta = (
      attachment: BlockAttachment
    ): {reportId?: number; id: number; fileName: string} | null => {
      const directId = Number(attachment?.id)
      const directReportId = Number(attachment?.reportId)
      if (Number.isFinite(directId) && directId > 0) {
        return {
          reportId: Number.isFinite(directReportId) && directReportId > 0 ? directReportId : undefined,
          id: directId,
          fileName: attachment.fileName || 'attachment',
        }
      }

      const attachmentFile = normalize(String(attachment.fileName || ''))
      const attachmentFileBase = normalizeBaseName(String(attachment.fileName || ''))
      const attachmentDoc = normalize(String(attachment.documentName || ''))

      const matched = uploadedFromApi.find((item) => {
        const serverFile = normalize(String(item.fileName || ''))
        const serverFileBase = normalizeBaseName(String(item.fileName || ''))
        const serverDoc = normalize(String(item.documentName || ''))

        const exactFile = !!attachmentFile && !!serverFile && attachmentFile === serverFile
        const exactDoc = !!attachmentDoc && !!serverDoc && attachmentDoc === serverDoc
        const baseFileMatch =
          !!attachmentFileBase &&
          !!serverFileBase &&
          (attachmentFileBase === serverFileBase ||
            attachmentFileBase.includes(serverFileBase) ||
            serverFileBase.includes(attachmentFileBase))

        return exactFile || exactDoc || baseFileMatch
      })

      const matchedId = Number(matched?.id)
      const matchedReportId = Number(matched?.reportId)
      if (Number.isFinite(matchedId) && matchedId > 0) {
        return {
          reportId: Number.isFinite(matchedReportId) && matchedReportId > 0 ? matchedReportId : undefined,
          id: matchedId,
          fileName: String(matched?.fileName || attachment.fileName || 'attachment'),
        }
      }

      // Only safe when a single file exists; otherwise "first file" would open the wrong document for other rows.
      if (readOnly && uploadedFromApi.length === 1) {
        const fallback = uploadedFromApi[0]
        const fallbackId = Number(fallback?.id)
        const fallbackReportId = Number(fallback?.reportId)
        if (Number.isFinite(fallbackId) && fallbackId > 0) {
          return {
            reportId:
              Number.isFinite(fallbackReportId) && fallbackReportId > 0 ? fallbackReportId : undefined,
            id: fallbackId,
            fileName: String(fallback?.fileName || attachment.fileName || 'attachment'),
          }
        }
      }

      return null
    }

    const attachmentItemsCache = new Map<number, any[]>()

    const getItemsForReportId = async (candidateReportId: number): Promise<any[]> => {
      if (attachmentItemsCache.has(candidateReportId)) {
        return attachmentItemsCache.get(candidateReportId) || []
      }

      const fromLoadedState =
        Number.isFinite(Number(attachmentSourceReportId)) &&
        Number(attachmentSourceReportId) === candidateReportId &&
        uploadedFromApi.length > 0
          ? uploadedFromApi
          : null

      if (fromLoadedState) {
        attachmentItemsCache.set(candidateReportId, fromLoadedState)
        return fromLoadedState
      }

      const items = await listReportAttachments(candidateReportId)
      const normalizedItems = Array.isArray(items) ? items : []
      attachmentItemsCache.set(candidateReportId, normalizedItems)
      return normalizedItems
    }

    const matchAttachmentFromItems = (attachment: BlockAttachment, items: any[]) => {
      const attachmentFile = normalize(String(attachment.fileName || ''))
      const attachmentFileBase = normalizeBaseName(String(attachment.fileName || ''))
      const attachmentDoc = normalize(String(attachment.documentName || ''))

      const matched = (items || []).find((item: any) => {
        const serverFile = normalize(String(item?.fileName || ''))
        const serverFileBase = normalizeBaseName(String(item?.fileName || ''))
        const serverDoc = normalize(String(item?.documentName || ''))

        const exactFile = !!attachmentFile && !!serverFile && attachmentFile === serverFile
        const exactDoc = !!attachmentDoc && !!serverDoc && attachmentDoc === serverDoc
        const baseFileMatch =
          !!attachmentFileBase &&
          !!serverFileBase &&
          (attachmentFileBase === serverFileBase ||
            attachmentFileBase.includes(serverFileBase) ||
            serverFileBase.includes(attachmentFileBase))

        return exactFile || exactDoc || baseFileMatch
      })

      const matchedId = Number(matched?.id)
      if (!Number.isFinite(matchedId) || matchedId <= 0) return null

      return {
        id: matchedId,
        fileName: String(matched?.fileName || attachment.fileName || 'attachment'),
      }
    }

    const resolveUploadedMetaAcrossCandidates = async (
      attachment: BlockAttachment
    ): Promise<{reportId: number; id: number; fileName: string} | null> => {
      const idCandidates = candidateReportIds.length > 0 ? [...candidateReportIds] : []
      if (idCandidates.length === 0 && Number.isFinite(reportId)) {
        idCandidates.push(Number(reportId))
      }

      const directId = Number(attachment?.id)
      const directReportId = Number((attachment as any)?.reportId)
      if (Number.isFinite(directId) && directId > 0) {
        const preferredIds: number[] = []

        if (Number.isFinite(directReportId) && directReportId > 0) {
          preferredIds.push(directReportId)
        }

        for (const candidate of idCandidates) {
          if (!preferredIds.includes(candidate)) {
            preferredIds.push(candidate)
          }
        }

        for (const candidateReportId of preferredIds) {
          const items = await getItemsForReportId(candidateReportId)
          const hasDirect = (items || []).some((item: any) => Number(item?.id) === directId)
          if (hasDirect) {
            const matchedById = (items || []).find((item: any) => Number(item?.id) === directId)
            return {
              reportId: candidateReportId,
              id: directId,
              fileName: String(matchedById?.fileName || attachment.fileName || 'attachment'),
            }
          }
        }

        if (Number.isFinite(reportId) && Number(reportId) > 0) {
          return {
            reportId: Number(reportId),
            id: directId,
            fileName: attachment.fileName || 'attachment',
          }
        }
      }

      for (const candidateReportId of idCandidates) {
        const items = await getItemsForReportId(candidateReportId)
        const matched = matchAttachmentFromItems(attachment, items)
        if (matched) {
          return {
            reportId: candidateReportId,
            id: matched.id,
            fileName: matched.fileName,
          }
        }
      }

      for (const candidateReportId of idCandidates) {
        const items = await getItemsForReportId(candidateReportId)
        const list = items || []
        if (list.length !== 1) continue
        const fallback = list[0]
        const fallbackId = Number(fallback?.id)
        if (Number.isFinite(fallbackId) && fallbackId > 0) {
          return {
            reportId: candidateReportId,
            id: fallbackId,
            fileName: String(fallback?.fileName || attachment.fileName || 'attachment'),
          }
        }
      }

      return null
    }

    return (
      <div className='border rounded p-3'>
        {blocks.map((block, blockIndex) => {
          const blockRows = rowsByBlock(block)
          const blockAttachments = attachmentsForBlock(block)

          return (
            <div key={`preview_bunker_block_${block}`} className='mb-4'>
              <div className='d-flex align-items-center justify-content-between mb-2'>
                <h6 className='fw-semibold text-dark mb-0'>Report {blockIndex + 1}</h6>
                <small className='text-muted'>Rows: {blockRows.length}</small>
              </div>

              <div className='table-responsive'>
                <table className='table table-striped table-bordered align-middle mb-0'>
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Date (UTC)</th>
                      <th>Time (UTC)</th>
                      <th>Port</th>
                      <th>Fuel Type</th>
                      <th>Quantity (MT)</th>
                      <th>Supplier</th>
                      <th>Sulfur %</th>
                      <th>Storage</th>
                      <th>Storage Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockRows.map((row, idx) => {
                      const isAllocationOnly = Boolean(row?.allocationOnly)
                      return (
                        <tr key={`preview_row_${block}_${idx}`}>
                          <td>{idx + 1}</td>
                          <td>{isAllocationOnly ? '--' : row?.dateUtc || '--'}</td>
                          <td>{isAllocationOnly ? '--' : row?.timeUtc || '--'}</td>
                          <td>{isAllocationOnly ? '--' : row?.port || '--'}</td>
                          <td>{isAllocationOnly ? '--' : row?.fuelType || '--'}</td>
                          <td>{isAllocationOnly ? '--' : row?.quantity || '--'}</td>
                          <td>{isAllocationOnly ? '--' : row?.supplierName || '--'}</td>
                          <td>{isAllocationOnly ? '--' : row?.sulfurPercentage || '--'}</td>
                          <td>{row?.storage || '--'}</td>
                          <td>{row?.storageQuantity || '--'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className='mt-3'>
                <h6 className='text-primary fw-bold text-uppercase mb-2'>Attachments</h6>
                <div className='table-responsive'>
                  <table className='table table-bordered align-middle mb-0'>
                    <thead>
                      <tr>
                        <th style={{minWidth: 240}}>Document</th>
                        <th>Remarks</th>
                        <th style={{minWidth: 180}}>File Name</th>
                        <th style={{width: 220}}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blockAttachments.map((attachment, aIdx) => {
                        const uploadedMeta = resolveUploadedMeta(attachment)
                        const pendingFile = resolvePendingFileForAttachment(attachment)
                        const canResolveFromServer =
                          readOnly ||
                          candidateReportIds.length > 0 ||
                          (Number.isFinite(Number(resolvedReportId)) && Number(resolvedReportId) > 0) ||
                          (Number.isFinite(Number(attachmentSourceReportId)) &&
                            Number(attachmentSourceReportId) > 0) ||
                          (Array.isArray(serverReportAttachments) && serverReportAttachments.length > 0)

                        return (
                          <tr key={`preview_attachment_${block}_${aIdx}`}>
                            <td>{attachment.documentName || '--'}</td>
                            <td>{attachment.remarks || '--'}</td>
                            <td>{attachment.fileName || '--'}</td>
                            <td>
                              {uploadedMeta ? (
                                <div className='d-flex gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    onClick={() =>
                                      void fetchReportAttachmentViewBlob(
                                        Number(uploadedMeta.reportId || reportId),
                                        uploadedMeta.id
                                      ).then(openBlobInNewTab)
                                    }
                                  >
                                    View
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-success'
                                    onClick={() =>
                                      void fetchReportAttachmentDownloadBlob(
                                        Number(uploadedMeta.reportId || reportId),
                                        uploadedMeta.id
                                      ).then((blob) =>
                                        downloadBlobAsFile(blob, uploadedMeta.fileName)
                                      )
                                    }
                                  >
                                    Download
                                  </button>
                                </div>
                              ) : pendingFile ? (
                                <div className='d-flex gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    onClick={() => viewPendingFile(pendingFile)}
                                  >
                                    View
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-success'
                                    onClick={() =>
                                      downloadPendingFile(
                                        pendingFile,
                                        String(attachment.fileName || pendingFile.name || 'attachment')
                                      )
                                    }
                                  >
                                    Download
                                  </button>
                                </div>
                              ) : canResolveFromServer &&
                                (attachment.fileName || attachment.documentName) ? (
                                <div className='d-flex gap-2'>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-primary'
                                    onClick={async () => {
                                      const resolved = await resolveUploadedMetaAcrossCandidates(attachment)
                                      if (!resolved) {
                                        toast.info('Attachment is not available for this report.')
                                        return
                                      }
                                      const blob = await fetchReportAttachmentViewBlob(
                                        resolved.reportId,
                                        resolved.id
                                      )
                                      openBlobInNewTab(blob)
                                    }}
                                  >
                                    View
                                  </button>
                                  <button
                                    type='button'
                                    className='btn btn-sm btn-light-success'
                                    onClick={async () => {
                                      const resolved = await resolveUploadedMetaAcrossCandidates(attachment)
                                      if (!resolved) {
                                        toast.info('Attachment is not available for this report.')
                                        return
                                      }
                                      const blob = await fetchReportAttachmentDownloadBlob(
                                        resolved.reportId,
                                        resolved.id
                                      )
                                      downloadBlobAsFile(blob, resolved.fileName)
                                    }}
                                  >
                                    Download
                                  </button>
                                </div>
                              ) : (
                                <span className='text-muted small'>--</span>
                              )}
                            </td>
                          </tr>
                        )
                      })}

                      {blockAttachments.length === 0 && (
                        <tr>
                          <td colSpan={4} className='text-center text-muted small py-4'>
                            No attachments added.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const handleAddRemark = async () => {
    if (remarkSubmitRef.current) return
    remarkSubmitRef.current = true
    setRemarkSubmitting(true)
    try {
      const legIdVal =
        selectedReport.legId ??
        selectedReport.legDto?.id ??
        selectedReport.leg?.id ??
        selectedVoyage?.legs?.find((l: any) => l.active)?.id ??
        selectedVoyage?.legs?.[0]?.id

      const reportTypeName =
        selectedReport.reportType ??
        selectedReport.name ??
        'Unnamed Report'

      const response = await updatedSubmittedReport(
        selectedReport.id,
        reportTypeName,
        ReportStatus.SUBMITTED,
        selectedReport.hasWarnings,
        selectedVoyage.id,
        Number(assignmentId),
        allValues,
        Number(vesselId),
        selectedReport.noOfWarnings,
        !!remarks.trim(), // hasRemarks
        remarks,
        undefined,
        undefined,
        undefined,
        undefined,
        legIdVal ? Number(legIdVal) : undefined
      )

      console.log(response)

      toast.success('Remark added successfully.')
      if (selectedReport) {
        selectedReport.remark = remarks
        selectedReport.hasReview = !!remarks.trim()
      }
      setRemarks('')
      setTimeout(() => {
        navigate('/operations/overview')
      }, 1500)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to add remark'))
    } finally {
      remarkSubmitRef.current = false
      setRemarkSubmitting(false)
    }
  }

  console.log('Report name: ', selectedReport?.name)

  // ✅ Helper to format coordinate into Degree° Minute′ Second″ Direction
  const formatCoordinate = (fields: [string, {value: any}][], baseLabel: string) => {
    const findValue = (suffix: string) => {
      const item = fields.find(([lbl]) => lbl.toLowerCase().includes(`${baseLabel} ${suffix}`))
      return item ? item[1]?.value?.toString().trim() || '' : ''
    }

    const deg = findValue('deg')
    const min = findValue('min')
    const sec = findValue('sec')
    const dir = findValue('direction')

    // ✅ If all are empty, return '--'
    if (!deg && !min && !sec && !dir) return '--'

    const pad2 = (v?: string) => {
      const s = String(v ?? '').trim()
      if (!s) return '00'
      return s.length === 1 ? `0${s}` : s
    }

    return `${deg || '0'}° ${pad2(min)}' ${pad2(sec)}" ${dir || ''}`.trim()
  }

  // ✅ Capitalize each word, preserving known acronyms
  const KNOWN_ACRONYMS = new Set([
    'ECA',
    'UTC',
    'ROB',
    'VLSFO',
    'LSMGO',
    'MGO',
    'HFO',
    'IFO',
    'ME',
    'AE',
    'CII',
    'RPM',
    'GPS',
    'ETA',
    'ETD',
    'EOSP',
    'COSP',
    'DPR',
  ])

  const capitalizeWords = (str: string) =>
    str
      .replace(/\s+/g, ' ')
      .trim()
      .split(' ')
      .map((word) => {
        const upper = word.toUpperCase()
        if (KNOWN_ACRONYMS.has(upper)) return upper
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      })
      .join(' ')

  // ✅ Get clean label with prefix before latitude/longitude and fixed typos
  const getCleanLabel = (label: string) => {
    let clean = String(label || '')
      .replace(/enrty/gi, 'Entry')
      .replace(/postion/gi, 'Position')
      .replace(/commensement/gi, 'Commencement')
      .replace(/\s+/g, ' ')
      .trim()

    const lower = clean.toLowerCase()

    if (lower.includes('latitude') && !lower.includes('position')) {
      const prefix = lower.split('latitude')[0].trim()
      return `${prefix ? capitalizeWords(prefix) + ' ' : ''}Latitude`
    }

    if (lower.includes('longitude') && !lower.includes('position')) {
      const prefix = lower.split('longitude')[0].trim()
      return `${prefix ? capitalizeWords(prefix) + ' ' : ''}Longitude`
    }

    return capitalizeWords(clean)
  }

  const handleDownload = () => {
    const pdfContainer = document.getElementById('pdf-preview')
    if (!pdfContainer) return

    pdfContainer.innerHTML = ''
    const content = document.createElement('div')

    // Start building PDF content — enterprise document chrome shared with OperationsList.
    const isBunkerExport = /\bbunker\b/i.test(
      String(selectedReport?.reportType ?? selectedReport?.name ?? '')
    )
    const isFuelChangeoverExport = isFuelChangeoverReport

    const exportMeta: ReportExportMeta = {
      title: readOnly ? selectedReport?.reportType : selectedReport?.name,
      vesselName: selectedVoyage?.vessel?.fleet_name,
      imoNumber: selectedVoyage?.vessel?.imoNumber,
      vesselType: selectedVoyage?.vessel?.vesselType,
      voyageNumber: selectedVoyage?.voyageNumber,
      departurePort: selectedVoyage?.departurePort,
      arrivalPort: selectedVoyage?.arrivalPort,
      reportDate: formattedReportDate || '--',
    }

    let html = renderReportDocumentStartHtml(exportMeta)

    // ✅ Loop through tabs — one styled section per tab, mirroring the on-screen view.
    orderedTabs.forEach((tab) => {
      const fieldValues = formData[tab.id] || {}
      const activeFields = (tab.fields || []).filter((field: any) => field.isActive)

      if (activeFields.length === 0) {
        html += renderSectionHtml(tab.name, renderNoFieldsBodyHtml())
        return
      }

      if (tab.name === 'Consumption & ROB') {
        html += renderSectionHtml(
          tab.name,
          renderConsumptionRobSectionHtml(consumptionData, {
            isFuelChangeoverReport: isFuelChangeoverExport,
          })
        )
        return
      }

      if (tab.name === 'Miscellaneous Consumables') {
        const mergedFields = sortFieldsByOrder(activeFields).map((field: any) => {
          const value = fieldValues?.[field.id]?.value ?? '--'
          return [field.label, value] as [string, any]
        })
        html += renderSectionHtml(tab.name, renderMiscConsumablesTableHtml(mergedFields))
        return
      }

      html += renderSectionHtml(
        tab.name,
        renderGenericTabBodyHtml(tab.fields || [], fieldValues, {isBunkerReport: isBunkerExport})
      )
    })

    html += renderReportDocumentEndHtml(exportMeta)

    // Append content to container
    content.innerHTML = html
    pdfContainer.appendChild(content)

    // ✅ Generate PDF
    setTimeout(() => {
      import('html2pdf.js').then((html2pdf) => {
        html2pdf
          .default()
          .from(pdfContainer)
          .set({
            margin: 0.5,
            filename: `${
              readOnly ? selectedReport?.reportType : selectedReport?.name
            }_${reportDateFileToken || 'report'}.pdf`,
            html2canvas: {scale: 2},
            pagebreak: {mode: ['css', 'legacy']},
            jsPDF: {unit: 'in', format: 'a4', orientation: 'portrait'},
          })
          .save()
      })
    }, 300)
  }

  if (!formData || !orderedTabs.length) {
    return (
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='d-flex flex-column flex-center min-h-500px p-10'>
          <div className='card card-flush shadow-sm p-10 text-center max-w-500px'>
            <div className='text-muted fs-3 mb-3 fw-bold'>No Report Data to Preview</div>
            <p className='text-gray-600 mb-6 fs-6'>
              Please select a report from the Operations overview or create a report to view its preview.
            </p>
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => navigate('/operations/overview')}
            >
              Back to Operations Overview
            </button>
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div
        className='d-flex flex-column flex-column-fluid pb-2 border-top'
        style={{height: '90vh'}}
      >
        <div
          id='kt_app_content'
          className='app-content flex-column-fluid d-flex flex-column '
          style={{flex: 1}}
        >
          <div className='card flex-column bg-white' style={{flex: 1}} ref={reportRef}>
            {/* Header */}

            <div className='card-header py-4 gap-2 gap-md-12 px-5'>
              <div>
                <div className='d-flex align-items-center gap-1 mb-4'>
                  <button
                    className='btn p-0 m-0'
                    onClick={() => {
                      if (readOnly) {
                        navigate('/operations/overview')
                      } else {
                        navigate(`/operations/create/${assignmentId}`, {
                          state: {
                            selectedTabId: activeAccordion || undefined,
                            selectedVoyage,
                            reportId: reportIdFromState || resolvedReportId || undefined,
                            formData,
                            consumptionPreviewData,
                            pendingBunkerAttachments,
                            stepCompletionStatus,
                            selectedReportName: selectedReport?.name || selectedReport?.reportType,
                          },
                        })
                      }
                    }}
                  >
                    <KTSVG
                      path='/media/icons/duotune/arrows/ArrowLeft.svg'
                      className='svg-icon-2'
                    />
                  </button>

                  <h3 className='card-title fw-bold text-dark m-0 p-0'>
                    {readOnly ? selectedReport?.reportType : selectedReport?.name}
                  </h3>
                </div>
                <div className='d-flex justify-content-between'>
                  <div className='d-flex gap-12'>
                    <p>
                      Vessel name:{' '}
                      <strong>
                        <VesselLandingLink
                          vesselId={selectedVoyage?.vessel?.id ?? null}
                          label={selectedVoyage?.vessel?.fleet_name}
                          className='text-dark text-decoration-underline'
                        />
                      </strong>
                    </p>
                    <p>
                      IMO number: <strong>{selectedVoyage?.vessel?.imoNumber}</strong>
                    </p>
                    <p>
                      Vessel type: <strong>{selectedVoyage?.vessel?.vesselType}</strong>
                    </p>
                  </div>
                </div>
              </div>
              <div onClick={handleDownload} style={{cursor: 'pointer'}}>
                <div className='d-flex gap-1'>
                  <KTSVG path='/media/map/DownloadSimple.svg' /> Download Report
                </div>
              </div>
            </div>
            <div
              id='pdf-preview-wrapper'
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: -1,
                opacity: 0,
                pointerEvents: 'none',
              }}
            >
              <div id='pdf-preview' />
            </div>
            {/* actual report */}
            <div className='d-flex gap-3 px-5 '>
              <div
                className=' pe-3 border-end py-3 overflow-y-auto'
                style={{flex: 0.7, maxHeight: '100vh', overflowY: auto}}
              >
                <div
                  className='p-3 list-group-item'
                  style={{backgroundColor: '#F4F9FF', maxWidth: '100%'}}
                >
                  <div className='title pb-1'>
                    {selectedVoyage?.voyageNumber || 'VM009'}:{' '}
                    {selectedVoyage?.departurePort || 'Mumbai'} -{' '}
                    {selectedVoyage?.arrivalPort || 'Singapore'}
                  </div>
                  {/* {selectedVoyage?.startDate && selectedVoyage?.endDate && (
                    <div className="details pb-1">
                      {new Date(selectedVoyage.startDate).toLocaleDateString('en-GB')} - {new Date(selectedVoyage.endDate).toLocaleDateString('en-GB')}
                    </div>
                  )} */}
                  {(selectedVoyage?.startDate || selectedVoyage?.endDate) && (
                    <div className='details pb-1'>
                      {selectedVoyage?.startDate && (
                        <span>
                          {new Date(selectedVoyage?.startDate).toLocaleDateString('en-GB')}
                        </span>
                      )}
                      <span> - </span>
                      {selectedVoyage?.endDate && (
                        <span>{new Date(selectedVoyage?.endDate).toLocaleDateString('en-GB')}</span>
                      )}
                    </div>
                  )}
                  {/* <div className='d-flex justify-content-between py-4 pe-5 gap-1'>
                                        <span className="badge" style={{ backgroundColor: '#FDF6B2' }}>Current</span>
                                        <div className='d-flex gap-1 align-items-center'>
                                            <KTSVG path='/media/map/Incomplete.svg' className='svg-icon-3' /><span className='text-muted'>12</span>
                                        </div>
                                    </div> */}
                </div>
                {orderedTabs.map((step) => (
                  <div
                    key={step.id}
                    onClick={() => setActiveAccordion(step.id)} // ✅ Update active accordion
                    className={`d-flex justify-content-between align-items-center mt-3 px-4 py-3 border rounded fw-bold step-item ${
                      activeAccordion === step.id ? 'current' : 'bg-light'
                    }`}
                    style={{
                      cursor: 'pointer',
                      paddingRight: '2rem',
                      fontSize: '16px',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <div>{step.name}</div>
                  </div>
                ))}
              </div>
              <div className='py-3' style={{flex: 2.6}}>
                <div className='accordion' id='tabsAccordion'>
                  {orderedTabs.map((tab, tabIndex) => {
                    const isBunkerReportView = /\bbunker\b/i.test(
                      String(selectedReport?.reportType ?? selectedReport?.name ?? "")
                    )
                    console.log(formData)
                    const valuesForTab = formData[tab.id] || {}
                    console.log(valuesForTab)
                    const allFieldLabels = Object.keys(valuesForTab)

                    // Optional: Fetch actual field metadata if available (labels, types, etc.)
                    // For now, we'll just assume `formData[tab.id]` should show all known fields.

                    // Create a list of all fields for the tab (could be static or fetched from props/config)
                    const fieldEntries = Object.entries(valuesForTab)

                    const ecaSelection = (() => {
                      if (formData) {
                        for (const tabData of Object.values(formData)) {
                          if (!tabData || typeof tabData !== 'object') continue
                          for (const [key, entry] of Object.entries(tabData as Record<string, any>)) {
                            const label = String(entry?.fieldLabel || entry?.label || key).toLowerCase()
                            if (
                              label.includes('eca type') ||
                              label.includes('event type') ||
                              label.includes('changeover direction') ||
                              label.includes('sequence step') ||
                              label.includes('eca event')
                            ) {
                              const val = String(entry?.value ?? entry ?? '').trim().toLowerCase()
                              if (val) return val
                            }
                          }
                        }
                      }
                      if (valuesForTab) {
                        for (const [k, v] of Object.entries(valuesForTab)) {
                          const fieldObj = (orderedTabs[tabIndex]?.fields || []).find((f: any) => String(f.id) === String(k))
                          const label = String(fieldObj?.label || '').toLowerCase()
                          if (label.includes('eca type')) {
                            const val = String((v as any)?.value ?? '').trim().toLowerCase()
                            if (val) return val
                          }
                        }
                      }
                      return ''
                    })()
                    const isExitSelected = ecaSelection.includes('exit')
                    const isEntrySelected = ecaSelection.includes('entry') || ecaSelection.includes('enter')

                    // Show all fields even if value is empty, but de-duplicate labels so empty mirror fields
                    // do not render twice when the same logical field is present in the template twice.
                    const dedupedFieldsForTab = (() => {
                      // Only render active fields, mirroring the edit form
                      // (CreateReport filters template fields by isActive). This
                      // prevents inactive/deleted fields from leaking into the view.
                      const sortedFields = sortFieldsByOrder(
                        (orderedTabs[tabIndex].fields || []).filter((field: any) => {
                          if (!field.isActive) return false
                          const l = String(field.label || '').toLowerCase().replace(/postion/g, 'position')
                          if (l.includes('eca exit')) {
                            if (isEntrySelected && !isExitSelected) return false
                          } else if (l.includes('eca entry') || l.includes('eca enrty')) {
                            if (isExitSelected && !isEntrySelected) return false
                          }
                          return true
                        })
                      )
                      const seen = new Map<string, any>()

                      sortedFields.forEach((field) => {
                        const labelKey = normalizePreviewFieldLabel(field.label) || `field-${field.id}`
                        const rawValue = valuesForTab[field.id]?.value ?? ''
                        const existing = seen.get(labelKey)

                        if (!existing) {
                          seen.set(labelKey, field)
                          return
                        }

                        const existingValue = valuesForTab[existing.id]?.value ?? ''
                        if (
                          hasMeaningfulPreviewValue(rawValue) &&
                          !hasMeaningfulPreviewValue(existingValue)
                        ) {
                          seen.set(labelKey, field)
                        }
                      })

                      return Array.from(seen.values())
                    })()

                    const allFieldsForTab = dedupedFieldsForTab

                    console.log('all fields for tab', allFieldsForTab)
                    // Merge default field list with form values
                    const mergedFields = allFieldsForTab.map((field) => {
                      const rawValue = valuesForTab[field.id]?.value ?? ''
                      let displayValue: string | JSX.Element = '--'


                      const hiddenBunkerScalarLabels = new Set([
                        'year',
                        'vessel',
                        'date (utc)',
                        'time (utc)',
                        "hfo (mt's)",
                        "mgo (mt's)",
                      ])

                      if (
                        isBunkerReportView &&
                        hiddenBunkerScalarLabels.has(String(field.label || '').trim().toLowerCase())
                      ) {
                        return null
                      }

                      if (isConsumptionMultiRowFieldLabel(field.label)) {
                        const arr = safeParseArray<any[]>(rawValue)

                        if (arr.length === 1 && !Array.isArray(arr[0])) {
                          displayValue = String(arr[0] ?? '--')
                        } else if (arr.length > 0) {
                          displayValue = (
                            <div>
                              {arr.map((row: any, i: number) => (
                                <div key={i}>
                                  <strong>Row {i + 1}:</strong>{' '}
                                  {Array.isArray(row)
                                    ? row.map((item) => formatConsumptionStoredValue(item)).join(', ')
                                    : formatConsumptionStoredValue(row)}
                                </div>
                              ))}
                            </div>
                          )
                        }
                      } else if (isBunkerReceivedRowsLabel(field.label)) {
                        const bunkerAttachmentsRaw =
                          allFieldsForTab
                            .map((tabField) => ({
                              label: tabField.label,
                              value: valuesForTab[tabField.id]?.value,
                            }))
                            .find((tabField) => isBunkerAttachmentLabel(tabField.label))?.value ?? null

                        const bunkerRows = renderBunkerReceivedRows(rawValue, bunkerAttachmentsRaw)
                        if (bunkerRows) {
                          displayValue = bunkerRows
                        }
                      } else if (isBunkerAttachmentLabel(field.label)) {
                        return null
                      } else {
                        const scalar = normalizeScalarForDisplay(rawValue, field)
                        displayValue = scalar === '' ? '--' : scalar
                      }
                      const fieldData = valuesForTab[field.id] ?? {} // use numeric field.id
                      return [field.label, {...fieldData, value: displayValue}]
                    }).filter(Boolean) as [string, {value: any}][]

                    console.log(mergedFields)

                    // ✅ Define the proper type for each field
                    type FieldTuple = [string, {value: any}]

                    // ✅ Initialize chunkedFields with the correct type
                    const chunkedFields: FieldTuple[][] = []

                    // Split mergedFields into chunks of size 4
                    const chunkSize = 4
                    for (let i = 0; i < mergedFields.length; i += chunkSize) {
                      chunkedFields.push(mergedFields.slice(i, i + chunkSize) as FieldTuple[])
                    }

                    const flatFields = chunkedFields.flat() as [string, {value: any}][]

                    // ✅ Only Deg, Min, Sec, Direction
                    const latitudeFields = flatFields.filter(([label]) =>
                      /latitude.*(deg|min|sec|direction)/i.test(label)
                    )

                    const longitudeFields = flatFields.filter(([label]) =>
                      /longitude.*(deg|min|sec|direction)/i.test(label)
                    )

                    // ✅ Only raw Latitude/Longitude fields (ending with Latitude or Longitude)
                    const chunkFields = flatFields.filter(
                      ([label]) =>
                        /latitude$/i.test(label.trim()) || /longitude$/i.test(label.trim())
                    )

                    // ✅ Step 3: Determine base label for formatting
                    const getBaseLabel = (
                      fields: [string, {value: any}][],
                      type: 'latitude' | 'longitude'
                    ) => {
                      if (fields.length === 0) return type
                      const firstLabel = fields[0][0].toLowerCase()
                      if (firstLabel.includes('start position')) return `start position ${type}`
                      if (firstLabel.includes('stop position')) return `stop position ${type}`
                      return type
                    }

                    const latitudeBase = getBaseLabel(latitudeFields, 'latitude')
                    const longitudeBase = getBaseLabel(longitudeFields, 'longitude')

                    // ✅ Step 4: Format once
                    
                    return (
                      <div className='accordion-item' key={tab.id}>
                        <h2 className='accordion-header' id={`heading-${tab.id}`}>
                          <button
                            className={`accordion-button ${
                              activeAccordion === tab.id ? '' : 'collapsed'
                            }`}
                            type='button'
                            onClick={() => setActiveAccordion((prev) => (prev === tab.id ? null : tab.id))}
                            data-bs-toggle='collapse'
                            data-bs-target={`#collapse-${tab.id}`}
                            aria-expanded={activeAccordion === tab.id}
                            aria-controls={`collapse-${tab.id}`}
                          >
                            <h5>{tab.name}</h5>
                            {!readOnly && (
                              <button
                                type='button'
                                className='btn'
                                onClick={() =>
                                  navigate(`/operations/create/${assignmentId}`, {
                                    state: {
                                      selectedTabId: tab.id,
                                      selectedVoyage,
                                      reportId: reportIdFromState || resolvedReportId || undefined,
                                      formData,
                                      consumptionPreviewData,
                                      pendingBunkerAttachments,
                                      stepCompletionStatus,
                                      selectedReportName:
                                        selectedReport?.name || selectedReport?.reportType,
                                    },
                                  })
                                }
                              >
                                <KTSVG path='/media/map/edit.svg' />{' '}
                                <span className='text-muted'>Edit</span>
                              </button>
                            )}
                          </button>
                        </h2>
                        <div
                          id={`collapse-${tab.id}`}
                          className={`accordion-collapse collapse ${
                            activeAccordion === tab.id ? 'show' : ''
                          }`}
                          aria-labelledby={`heading-${tab.id}`}
                          data-bs-parent='#tabsAccordion'
                        >
                          <div className='accordion-body' style={{maxWidth: '100%'}}>
                            {allFieldsForTab.length === 0 ? (
                              <div className='text-muted px-5 py-3'>No fields available.</div>
                            ) : tab.name === 'Consumption & ROB' ? (
                              consumptionLoading ? (
                                <div className='text-muted px-5 py-3'>
                                  Loading consumption data...
                                </div>
                              ) : consumptionError ? (
                                <div className='alert alert-warning'>{consumptionError}</div>
                              ) : !consumptionData ? (
                                <div className='text-muted px-5 py-3'>
                                  No consumption data available for this report.
                                </div>
                              ) : (
                                <div className='mb-4'>
                                  {consumptionData?.remarks ? (
                                    <div className='alert alert-light border mb-4 mx-5'>
                                      <div className='text-muted mb-1 text-start'>Remarks:</div>
                                      <strong>{consumptionData.remarks}</strong>
                                    </div>
                                  ) : null}
                                  {isFuelChangeoverReport ? (
                                    <div>
                                      {/* 4 KPI Summary Cards */}
                                      {(() => {
                                        const tanks: any[] =
                                          Array.isArray(consumptionData?.tanks) && consumptionData.tanks.length > 0
                                            ? consumptionData.tanks
                                            : synthesizeTankWiseFuelRows(consumptionData?.rows || [])
                                        let totalVlsfo = 0
                                        let totalLsmgo = 0
                                        let totalConsumed = 0
                                        let activeServiceTankSulphur = ''
                                        let isEcaCompliant = false
                                        const hasData = tanks.length > 0

                                        tanks.forEach((t) => {
                                          const cur = Number(t.currentRob) || 0
                                          const cons = Number(t.consumedTransferred) || 0
                                          totalConsumed += cons
                                          const isLsmgo =
                                            t.fuelType === 'LSMGO' ||
                                            t.fuelType === 'MGO' ||
                                            t.fuelType === 'ULSFO' ||
                                            String(t.q88Grade).includes('0.1%') ||
                                            String(t.tankName).toUpperCase().includes('LSMGO') ||
                                            (parseFloat(t.sulphurPercentage) <= 0.10 && parseFloat(t.sulphurPercentage) > 0)

                                          if (isLsmgo) {
                                            totalLsmgo += cur
                                          } else {
                                            totalVlsfo += cur
                                          }

                                          if (
                                            String(t.allocationStatus).toLowerCase().includes('active') ||
                                            String(t.allocationStatus).toLowerCase().includes('in service') ||
                                            String(t.tankName).toLowerCase().includes('service')
                                          ) {
                                            activeServiceTankSulphur = t.sulphurPercentage
                                            const sNum = parseFloat(t.sulphurPercentage)
                                            if (Number.isFinite(sNum) && sNum <= 0.10) {
                                              isEcaCompliant = true
                                            }
                                          }
                                        })

                                        const resolvedActiveSulphur =
                                          activeServiceTankSulphur ||
                                          (totalLsmgo > 0 ? '0.09%' : totalVlsfo > 0 ? '0.48%' : '--')

                                        return (
                                          <>
                                            <div className='row g-4 mb-5 px-5'>
                                              <div className='col-md-3'>
                                                <div className='card bg-light-primary border border-primary border-opacity-25 shadow-sm p-4 h-100'>
                                                  <div className='text-muted small fw-bold text-uppercase'>
                                                    Total VLSFO ROB
                                                  </div>
                                                  <div className='fs-2hx fw-bolder text-primary my-1'>
                                                    {totalVlsfo.toFixed(2)} MT
                                                  </div>
                                                  <div className='text-muted fs-7'>
                                                    Pre-changeover HFO/VLSFO Service &amp; Settling
                                                  </div>
                                                </div>
                                              </div>
                                              <div className='col-md-3'>
                                                <div className='card bg-light-success border border-success border-opacity-25 shadow-sm p-4 h-100'>
                                                  <div className='text-muted small fw-bold text-uppercase'>
                                                    Total LSMGO ROB (ECA)
                                                  </div>
                                                  <div className='fs-2hx fw-bolder text-success my-1'>
                                                    {totalLsmgo.toFixed(2)} MT
                                                  </div>
                                                  <div className='text-muted fs-7'>
                                                    ECA Compliant Low Sulphur MGO Fuel
                                                  </div>
                                                </div>
                                              </div>
                                              <div className='col-md-3'>
                                                <div className='card bg-light-info border border-info border-opacity-25 shadow-sm p-4 h-100'>
                                                  <div className='text-muted small fw-bold text-uppercase'>
                                                    Total Consumed / Transferred
                                                  </div>
                                                  <div className='fs-2hx fw-bolder text-info my-1'>
                                                    {totalConsumed.toFixed(2)} MT
                                                  </div>
                                                  <div className='text-muted fs-7'>
                                                    Voyage Consumption &amp; Changeover Flushing
                                                  </div>
                                                </div>
                                              </div>
                                              <div className='col-md-3'>
                                                <div className={`card ${isEcaCompliant ? 'bg-light-success border-success' : 'bg-light-warning border-warning'} border border-opacity-25 shadow-sm p-4 h-100`}>
                                                  <div className='text-muted small fw-bold text-uppercase'>
                                                    ECA Changeover Status
                                                  </div>
                                                  <div className='my-1'>
                                                    {isEcaCompliant ? (
                                                      <span className='badge badge-success fs-7 fw-bold px-3 py-2'>
                                                        COMPLIANT (≤ 0.10% S)
                                                      </span>
                                                    ) : hasData ? (
                                                      <span className='badge badge-warning fs-7 fw-bold px-3 py-2'>
                                                        PRE-CHANGEOVER ({resolvedActiveSulphur})
                                                      </span>
                                                    ) : (
                                                      <span className='badge badge-secondary fs-7 fw-bold px-3 py-2'>
                                                        PENDING TELEMETRY
                                                      </span>
                                                    )}
                                                  </div>
                                                  <div className='text-muted fs-7'>
                                                    {hasData
                                                      ? `Active Service Tank Sulphur ${resolvedActiveSulphur}`
                                                      : 'No position reports submitted for this voyage'}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>

                                            {/* 9-column Tank-Wise Table */}
                                            {tanks.length > 0 ? (
                                              <div className='card border mb-5 mx-5 shadow-sm'>
                                                <div className='card-header py-3 bg-light'>
                                                  <h5 className='card-title fw-bold text-dark m-0'>
                                                    Tank-Wise Fuel Allocation &amp; Q88 Grades
                                                  </h5>
                                                </div>
                                                <div className='card-body p-0 table-responsive'>
                                                  <table className='table table-bordered table-hover align-middle mb-0 text-center fs-7'>
                                                    <thead className='table-dark text-uppercase'>
                                                      <tr>
                                                        <th style={{width: '40px'}}>#</th>
                                                        <th style={{minWidth: '180px'}} className='text-start'>
                                                          TANK NAME
                                                        </th>
                                                        <th style={{minWidth: '170px'}} className='text-start'>
                                                          Q88 GRADE
                                                        </th>
                                                        <th style={{minWidth: '90px'}}>FUEL TYPE</th>
                                                        <th style={{minWidth: '110px'}}>SOUNDING ROB (MT)</th>
                                                        <th style={{minWidth: '120px'}}>
                                                          CONSUMED / TRANSFERRED (MT)
                                                        </th>
                                                        <th style={{minWidth: '110px'}}>CURRENT ROB (MT)</th>
                                                        <th style={{minWidth: '90px'}}>SULPHUR %</th>
                                                        <th style={{minWidth: '160px'}}>ALLOCATION STATUS</th>
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {tanks.map((tRow: any, tIdx: number) => (
                                                        <tr key={tIdx}>
                                                          <td className='fw-bold text-muted'>{tIdx + 1}</td>
                                                          <td className='text-start fw-semibold text-dark'>
                                                            {tRow.tankName}
                                                          </td>
                                                          <td className='text-start text-primary fw-semibold'>
                                                            {tRow.q88Grade}
                                                          </td>
                                                          <td>
                                                            <span className='badge badge-light-primary fw-bold'>
                                                              {tRow.fuelType}
                                                            </span>
                                                          </td>
                                                          <td className='text-end fw-semibold pe-3'>
                                                            {Number(tRow.soundingRob).toFixed(2)}
                                                          </td>
                                                          <td className='text-end fw-semibold pe-3 text-danger'>
                                                            {Number(tRow.consumedTransferred) > 0
                                                              ? Number(tRow.consumedTransferred).toFixed(2)
                                                              : '0.00'}
                                                          </td>
                                                          <td className='text-end fw-bolder pe-3 text-success'>
                                                            {Number(tRow.currentRob).toFixed(2)}
                                                          </td>
                                                          <td>
                                                            <span className='badge badge-light-info fw-bold'>
                                                              {tRow.sulphurPercentage}
                                                            </span>
                                                          </td>
                                                          <td>
                                                            <span
                                                              className={`badge ${
                                                                String(tRow.allocationStatus).includes('Active')
                                                                  ? 'badge-light-success'
                                                                  : String(tRow.allocationStatus).includes('In Service')
                                                                  ? 'badge-light-warning'
                                                                  : String(tRow.allocationStatus).includes('ECA')
                                                                  ? 'badge-light-info'
                                                                  : 'badge-light-secondary'
                                                              } fw-bold`}
                                                            >
                                                              {tRow.allocationStatus}
                                                            </span>
                                                          </td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </table>
                                                </div>
                                              </div>
                                            ) : null}
                                          </>
                                        )
                                      })()}
                                    </div>
                                  ) : consumptionData?.rows?.length ? (
                                    <>
                                      {consumptionData.rows.map((r: any, fuelIndex: number) => (
                                        <div
                                          key={fuelIndex}
                                          className='mb-4 border rounded p-3 m-5 shadow-sm bg-white'
                                        >
                                          <div className='row px-5 py-4 text-start'>
                                            <div className='row mb-2'>
                                              {r.tankName ? (
                                                <div className='col-md-3 mb-2'>
                                                  <div className='text-muted mb-1 text-start'>
                                                    Tank Name:
                                                  </div>
                                                  <strong className='text-dark'>{r.tankName}</strong>
                                                </div>
                                              ) : null}
                                              {r.q88Grade ? (
                                                <div className='col-md-3 mb-2'>
                                                  <div className='text-muted mb-1 text-start'>
                                                    Q88 Grade:
                                                  </div>
                                                  <strong className='text-primary'>{r.q88Grade}</strong>
                                                </div>
                                              ) : null}
                                              <div className='col-md-2 mb-2'>
                                                <div className='text-muted mb-1 text-start'>
                                                  Fuel Type:
                                                </div>
                                                <strong>{r.fieldType || r.fuelType || '--'}</strong>
                                              </div>
                                              {r.sulphurPercentage ? (
                                                <div className='col-md-2 mb-2'>
                                                  <div className='text-muted mb-1 text-start'>
                                                    Sulphur %:
                                                  </div>
                                                  <span className='badge badge-light-info fw-bold'>
                                                    {r.sulphurPercentage}
                                                  </span>
                                                </div>
                                              ) : null}
                                              {r.allocationStatus ? (
                                                <div className='col-md-2 mb-2'>
                                                  <div className='text-muted mb-1 text-start'>
                                                    Status:
                                                  </div>
                                                  <span className='badge badge-light-secondary'>
                                                    {r.allocationStatus}
                                                  </span>
                                                </div>
                                              ) : null}
                                            </div>
                                            <div className='row mb-3 bg-light rounded py-2'>
                                              <div className='col-md-6 mb-1'>
                                                <div className='text-muted mb-1 text-start'>
                                                  Opening / Initial ROB:
                                                </div>
                                                <strong>{formatConsumptionMetric(r.initialRob)} MT</strong>
                                              </div>
                                              <div className='col-md-6 mb-1'>
                                                <div className='text-muted mb-1'>Current / Last ROB:</div>
                                                <strong>
                                                  {formatConsumptionMetric(r.lastRob ?? r.rob)} MT
                                                </strong>
                                              </div>
                                            </div>
                                            <div className='row'>
                                              <div className='col-md-12 mb-3'>
                                                <div className='text-muted text-start mb-3 fw-semibold'>
                                                  Machinery Breakdown:
                                                </div>
                                                {(r.machineries || []).length === 0 ? (
                                                  <div className='text-muted'>
                                                    No machinery data
                                                  </div>
                                                ) : (
                                                  (r.machineries || []).map(
                                                    (mach: any, machIndex: number) => (
                                                      <div
                                                        key={machIndex}
                                                        className='row mb-3 px-3'
                                                      >
                                                        {(() => {
                                                          const machineName = String(
                                                            mach.machinery ?? ''
                                                          )
                                                            .trim()
                                                            .toLowerCase()
                                                          const machineKey = machineName.replace(
                                                            /[\s_-]+/g,
                                                            ''
                                                          )
                                                          const showOperationType =
                                                            machineKey === 'boiler1' ||
                                                            machineKey === 'boiler2' ||
                                                            machineKey === 'ae1' ||
                                                            machineKey === 'ae2'

                                                          return (
                                                            <>
                                                              <div className='col-md-3 mb-2'>
                                                                <div className='text-muted text-start mb-1'>
                                                                  Name:
                                                                </div>
                                                                <strong>
                                                                  {mach.machinery || '--'}
                                                                </strong>
                                                              </div>
                                                              <div className='col-md-3 mb-2'>
                                                                <div className='text-muted text-start mb-1'>
                                                                  Consumed:
                                                                </div>
                                                                <strong>
                                                                  {formatConsumptionMetric(mach.consumed)} MT
                                                                </strong>
                                                              </div>
                                                              <div className='col-md-3 mb-2'>
                                                                <div className='text-muted text-start mb-1'>
                                                                  ROB:
                                                                </div>
                                                                <strong>{formatConsumptionMetric(mach.rob)} MT</strong>
                                                              </div>
                                                              <div className='col-md-3 mb-2'>
                                                                <div className='text-muted text-start mb-1'>
                                                                  Operation Type:
                                                                </div>
                                                                <strong>
                                                                  {showOperationType
                                                                    ? mach.operationType || '--'
                                                                    : '--'}
                                                                </strong>
                                                              </div>
                                                            </>
                                                          )
                                                        })()}
                                                      </div>
                                                    )
                                                  )
                                                )}
                                              </div>
                                            </div>
                                            <div className='row'>
                                              <div className='col-md-12 mb-2 text-end'>
                                                <span className='text-muted me-2'>
                                                  Total Consumption:
                                                </span>
                                                <strong>
                                                  {(() => {
                                                    const total = (r.machineries || []).reduce(
                                                      (sum: number, m: any) => {
                                                        const n = Number(m?.consumed)
                                                        return sum + (Number.isFinite(n) ? n : 0)
                                                      },
                                                      0
                                                    )
                                                    return `${formatConsumptionMetric(total)} MT`
                                                  })()}
                                                </strong>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  ) : (
                                    <div className='text-muted px-5 py-3'>
                                      No consumption data available.
                                    </div>
                                  )}
                                  {consumptionData?.lubeOils?.length ? (
                                    <div className='mb-4 border rounded p-3 m-5'>
                                      <div className='text-muted mb-3 text-start fw-bold'>
                                        Lube Oil Status
                                      </div>
                                      <div className='table-responsive'>
                                        <table className='table table-bordered table-row-bordered align-middle mb-0'>
                                          <thead>
                                            <tr className='fw-bold text-muted bg-light'>
                                              <th className='text-center'>Sr. No</th>
                                              <th>Grades</th>
                                              <th className='text-center'>Opening Stock</th>
                                              <th className='text-center'>Received</th>
                                              <th className='text-center'>Consumed by Vessel</th>
                                              <th className='text-center'>Closing Stock</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {consumptionData.lubeOils.map(
                                              (lube: any, lubeIndex: number) => (
                                                <tr key={lubeIndex}>
                                                  <td className='text-center'>{lubeIndex + 1}</td>
                                                  <td>{lube.grade || '--'}</td>
                                                  <td className='text-center'>
                                                    {formatConsumptionMetric(lube.openingStock)}
                                                  </td>
                                                  <td className='text-center'>
                                                    {formatConsumptionMetric(lube.received)}
                                                  </td>
                                                  <td className='text-center'>
                                                    {formatConsumptionMetric(lube.consumedByVessel)}
                                                  </td>
                                                  <td className='text-center'>
                                                    {formatConsumptionMetric(lube.closingStock)}
                                                  </td>
                                                </tr>
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              )
                            ) : tab.name === 'Miscellaneous Consumables' ? (
                              <div className='table-responsive px-5 py-3'>
                                <table className='table table-bordered table-row-bordered align-middle mb-0'>
                                  <thead>
                                    <tr className='fw-bold text-muted bg-light'>
                                      <th>Sr. No</th>
                                      <th>Parameter</th>
                                      <th>Value</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {mergedFields.map(([label, fieldData], idx) => (
                                      <tr key={idx}>
                                        <td>{idx + 1} </td>
                                        <td>{getCleanLabel(label)}</td>
                                        <td>
                                          {typeof fieldData?.value === 'string' ||
                                          typeof fieldData?.value === 'number' ? (
                                            <strong>{fieldData?.value || '--'}</strong>
                                          ) : (
                                            fieldData?.value || '--'
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div>
                                {(() => {
                                  type FieldTuple = [string, {value: any}]
                                  const all = chunkedFields.flat() as FieldTuple[]

                                  const norm = (s: string) =>
                                    s.toLowerCase().replace(/postion/g, 'position') // fix typo
                                  const isPart = (l: string) => /(deg|min|sec|direction)/i.test(l)

                                  const pick = (
                                    kind: 'latitude' | 'longitude',
                                    which: 'start' | 'stop' | 'generic'
                                  ) =>
                                    all.filter(([label]) => {
                                      const l = norm(label)
                                      if (!(l.includes(kind) && isPart(l))) return false
                                      if (which === 'start') return l.includes('start')
                                      if (which === 'stop') return l.includes('stop')
                                      return !l.includes('start') && !l.includes('stop')
                                    })

                                  // Split into Start / Stop / Generic
                                  const latStart = pick('latitude', 'start')
                                  const latStop = pick('latitude', 'stop')
                                  const latGeneric = pick('latitude', 'generic')

                                  const lonStart = pick('longitude', 'start')
                                  const lonStop = pick('longitude', 'stop')
                                  const lonGeneric = pick('longitude', 'generic')

                                  const hasLat =
                                    latStart.length > 0 || latStop.length > 0 || latGeneric.length > 0
                                  const hasLon =
                                    lonStart.length > 0 || lonStop.length > 0 || lonGeneric.length > 0

                                  return (
                                    <>
                                      {/* Row 1: Latitude - Start | Stop */}
                                      {hasLat && (
                                        <div className='row mb-3 px-5 py-3'>
                                          {(latStart.length > 0 || latGeneric.length > 0) && (
                                            <div className='col-md-3'>
                                              <div className='text-muted mb-1'>
                                                {latStart.length
                                                  ? 'Start Position Latitude'
                                                  : 'Latitude'}
                                                :
                                              </div>
                                              <strong>
                                                {formatCoordinate(
                                                  latStart.length ? latStart : latGeneric,
                                                  latStart.length
                                                    ? 'start position latitude'
                                                    : 'latitude'
                                                )}
                                              </strong>
                                            </div>
                                          )}

                                          {latStop.length > 0 && (
                                            <div className='col-md-3'>
                                              <div className='text-muted mb-1'>
                                                Stop Position Latitude:
                                              </div>
                                              <strong>
                                                {formatCoordinate(
                                                  latStop,
                                                  'stop position latitude'
                                                )}
                                              </strong>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Row 2: Longitude - Start | Stop */}
                                      {hasLon && (
                                        <div className='row mb-3 px-5 py-3'>
                                          {(lonStart.length > 0 || lonGeneric.length > 0) && (
                                            <div className='col-md-3'>
                                              <div className='text-muted mb-1'>
                                                {lonStart.length
                                                  ? 'Start Position Longitude'
                                                  : 'Longitude'}
                                                :
                                              </div>
                                              <strong>
                                                {formatCoordinate(
                                                  lonStart.length ? lonStart : lonGeneric,
                                                  lonStart.length
                                                    ? 'start position longitude'
                                                    : 'longitude'
                                                )}
                                              </strong>
                                            </div>
                                          )}

                                          {lonStop.length > 0 && (
                                            <div className='col-md-3'>
                                              <div className='text-muted mb-1'>
                                                Stop Position Longitude:
                                              </div>
                                              <strong>
                                                {formatCoordinate(
                                                  lonStop,
                                                  'stop position longitude'
                                                )}
                                              </strong>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </>
                                  )
                                })()}

                                {/* ✅ Render other fields in chunks */}
                                {chunkedFields.map((chunk, rowIndex) => {
                                  const typedChunk = chunk as [string, {value: any}][]

                                  return (
                                    <div className='row mb-3 px-5 py-3' key={rowIndex}>
                                      {typedChunk
                                        // ✅ Exclude only Deg/Min/Sec/Direction, NOT raw Latitude/Longitude
                                        .filter(
                                          ([label]) =>
                                            !/latitude.*(deg|min|sec|direction)/i.test(label) &&
                                            !/longitude.*(deg|min|sec|direction)/i.test(label)
                                        )
                                        .map(([label, fieldData], idx) => {
                                          const isFullWidthField =
                                            (isBunkerReceivedRowsLabel(label) ||
                                              isBunkerAttachmentLabel(label)) &&
                                            !(
                                              typeof fieldData?.value === 'string' ||
                                              typeof fieldData?.value === 'number'
                                            )

                                          const isPositionOrCoordinate =
                                            typeof fieldData?.value === 'string' &&
                                            (fieldData.value.includes('°') ||
                                             /position|latitude|longitude/i.test(label))

                                          return (
                                          <div className={isFullWidthField ? 'col-12 mb-2' : isPositionOrCoordinate ? 'col-md-4 mb-2' : 'col-md-3 mb-2'} key={idx}>
                                            <div className='text-muted mb-1'>
                                              {getCleanLabel(label)}:
                                            </div>
                                            {typeof fieldData?.value === 'string' ||
                                            typeof fieldData?.value === 'number' ? (
                                              <strong className='text-gray-800 fs-6'>{fieldData?.value || '--'}</strong>
                                            ) : (
                                              fieldData?.value || '--'
                                            )}
                                          </div>
                                        )})}
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                {shouldShowRemarksSection && (
                  <div className='px-5 py-4 border-top mt-4'>
                    <h5 className='fw-bold text-gray-800 mb-3'>Remarks</h5>

                    {displayRemark ? (
                      <div className='bg-light-primary border border-primary border-dashed rounded p-4'>
                        <div className='fw-semibold text-primary fs-7 mb-1'>Submitted Remark:</div>
                        <div className='text-gray-800 fs-6' style={{whiteSpace: 'pre-wrap'}}>{displayRemark}</div>
                      </div>
                    ) : canAddReviewRemark ? (
                      <>
                        <textarea
                          className='form-control mb-3'
                          rows={3}
                          placeholder='Enter your remarks here...'
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                        />
                        <button className='btn btn-primary' onClick={handleAddRemark} disabled={remarkSubmitting}>
                          {remarkSubmitting ? 'Saving...' : 'Add Remark'}
                        </button>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {PreviewReportModal}
